import { useState, useEffect, useCallback, useMemo } from 'react';
import { dashboardHandler, CustomDateParams, createCancelSource, isRequestCancelled } from '../DashboardHandler';
import type { GetStatisticsCommandResponse } from '../../../../Core/Api/Api';
import type { CustomDateRange } from '../types';
import { getComparisonDays } from '../Components/DateRangePicker';

interface UseDashboardDataReturn {
  statsData: GetStatisticsCommandResponse | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

export const useDashboardData = (customRange?: CustomDateRange): UseDashboardDataReturn => {
  const [statsData, setStatsData] = useState<GetStatisticsCommandResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const cacheKey = useMemo(() => {
    if (customRange) {
      const compDays = getComparisonDays(customRange);
      return `stats:custom:${customRange.startDate}:${customRange.endDate}:${compDays ?? 'auto'}`;
    }
    return 'stats:default';
  }, [customRange]);

  useEffect(() => {
    const source = createCancelSource();
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        let customDate: CustomDateParams | undefined;
        if (customRange) {
          customDate = {
            startDate: customRange.startDate,
            endDate: customRange.endDate,
            comparisonDays: getComparisonDays(customRange),
          };
        }

        const statistics = await dashboardHandler.getStatisticData(source.token, customDate);

        if (isMounted) {
          setStatsData(statistics);
        }
      } catch (err: any) {
        if (isMounted && !isRequestCancelled(err)) {
          setError(err.message || 'Failed to load dashboard data');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      source.cancel('Component unmounted');
    };
  }, [retryCount, cacheKey]);

  const retry = useCallback(() => setRetryCount((c) => c + 1), []);

  return { statsData, loading, error, retry };
};
